import React, { useState } from "react";
import styled from "styled-components";

// InnerLogic Brand Colors
const brandColors = {
  primary: {
    50: "#EBF1FD",
    100: "#D5E2F9", 
    500: "#2D70E2",
    600: "#2254AA",
  },
  secondary: {
    500: "#27CDA7",
    600: "#1D9A7D",
  },
  text: {
    dark: "#2A3039",
    gray: "#666D79",
    light: "#8B93A3",
  },
  background: {
    white: "#FFFFFF",
    lightBlue: "#F8FAFF",
  },
  neutral: {
    200: "#F2F2F2",
    500: "#DFDFDF",
  },
  status: {
    success: "#10B981",
    warning: "#F59E0B",
    error: "#EF4444",
  }
};

const brandGradient = `linear-gradient(135deg, ${brandColors.primary["500"]} 0%, ${brandColors.secondary["500"]} 100%)`;

// Styled Components
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: "Raleway", sans-serif;
  background: ${brandColors.background.white};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: ${brandColors.background.white};
  box-shadow: 0 1px 3px rgba(45, 112, 226, 0.1);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: ${brandColors.text.gray};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${brandColors.primary["50"]};
    color: ${brandColors.primary["500"]};
  }
`;

const HeaderTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${brandGradient};
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;
  box-shadow: 0 2px 8px rgba(45, 112, 226, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(45, 112, 226, 0.25);
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const KPISection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const KPICard = styled.div`
  background: ${brandColors.background.white};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(45, 112, 226, 0.08);
  border: 1px solid ${brandColors.primary["100"]};
`;

const KPIValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.color || brandColors.text.dark};
  margin-bottom: 8px;
`;

const KPILabel = styled.div`
  font-size: 14px;
  color: ${brandColors.text.gray};
  margin-bottom: 4px;
`;

const KPIChange = styled.div`
  font-size: 12px;
  color: ${props => props.positive ? brandColors.status.success : brandColors.status.error};
  font-weight: 600;
`;

const FilterSection = styled.div`
  background: ${brandColors.background.white};
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(45, 112, 226, 0.08);
  border: 1px solid ${brandColors.primary["100"]};
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  white-space: nowrap;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 2px solid ${brandColors.neutral["200"]};
  border-radius: 6px;
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  background: white;
  min-width: 120px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 2px solid ${brandColors.neutral["200"]};
  border-radius: 6px;
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  min-width: 200px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }

  &::placeholder {
    color: ${brandColors.text.light};
  }
`;

const PlansGrid = styled.div`
  background: ${brandColors.background.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(45, 112, 226, 0.08);
  border: 1px solid ${brandColors.primary["100"]};
`;

const GridHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: ${brandColors.primary["50"]};
  border-bottom: 1px solid ${brandColors.primary["100"]};
`;

const GridTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0;
`;

const GridActions = styled.div`
  display: flex;
  gap: 12px;
`;

const GridButton = styled.button`
  padding: 8px 16px;
  border: 2px solid ${brandColors.primary["500"]};
  border-radius: 6px;
  background: transparent;
  color: ${brandColors.primary["500"]};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${brandColors.primary["500"]};
    color: white;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 16px 24px;
  background: ${brandColors.background.white};
  color: ${brandColors.text.dark};
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid ${brandColors.neutral["200"]};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${brandColors.neutral["200"]};
  transition: background-color 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: ${brandColors.primary["50"]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px 24px;
  font-size: 14px;
  color: ${brandColors.text.dark};
  vertical-align: middle;
`;

const StatusChip = styled.span`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'on-track': return brandColors.status.success + '20';
      case 'at-risk': return brandColors.status.warning + '20';
      case 'off-track': return brandColors.status.error + '20';
      default: return brandColors.neutral["200"];
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'on-track': return brandColors.status.success;
      case 'at-risk': return brandColors.status.warning;
      case 'off-track': return brandColors.status.error;
      default: return brandColors.text.gray;
    }
  }};
`;

const ProgressBar = styled.div`
  width: 100px;
  height: 8px;
  background: ${brandColors.neutral["200"]};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => {
    if (props.progress >= 70) return brandColors.status.success;
    if (props.progress >= 40) return brandColors.status.warning;
    return brandColors.status.error;
  }};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${brandColors.text.gray};
`;

const Sparkline = styled.div`
  width: 60px;
  height: 20px;
  background: ${brandColors.primary["50"]};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: ${props => props.trend || 60}%;
    background: ${brandColors.secondary["500"]};
    transition: height 0.3s ease;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 24px;
`;

const TabList = styled.div`
  display: flex;
  background: ${brandColors.background.white};
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 2px 12px rgba(45, 112, 226, 0.08);
  border: 1px solid ${brandColors.primary["100"]};
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: ${props => props.active ? brandGradient : 'transparent'};
  color: ${props => props.active ? 'white' : brandColors.text.gray};
  font-weight: 600;
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${props => props.active ? brandGradient : brandColors.primary["50"]};
    color: ${props => props.active ? 'white' : brandColors.primary["500"]};
  }
`;

const ActionPlanDashboard = ({ 
  groups, 
  plans, 
  selectedGroup, 
  onBack, 
  onGroupSelect, 
  onPlanSelect, 
  onCreateNew 
}) => {


  const getDaysAgo = (dateString) => {
    // For prototype, use realistic recent dates
    const today = new Date();
    const groups = [
      { id: 1, daysAgo: 3 },
      { id: 2, daysAgo: 12 }
    ];
    const group = groups.find(g => g.id === parseInt(dateString.split('-')[2]));
    return group ? group.daysAgo : Math.floor(Math.random() * 30) + 1; // 1-30 days for new groups
  };

  const getOnTrackPercentage = (group) => {
    const total = group.on_track + group.at_risk + group.off_track;
    if (total === 0) return 0;
    return Math.round((group.on_track / total) * 100);
  };



  return (
    <Container>
      <HeaderSection>
        <BackButton onClick={onBack}>
          <span>←</span>
          Back to Hub
        </BackButton>
        <HeaderTitle>Planning Groups</HeaderTitle>
        <CreateButton onClick={onCreateNew}>
          <span>+</span>
          Create New Group
        </CreateButton>
      </HeaderSection>

      <ContentWrapper>
        <PlansGrid>
          <GridHeader>
            <GridTitle>Planning Groups ({groups.length})</GridTitle>
            <GridActions>
            </GridActions>
          </GridHeader>
          
          <Table>
            <thead>
              <tr>
                <TableHeader>Group Name</TableHeader>
                <TableHeader>Last Updated</TableHeader>
                <TableHeader>Members</TableHeader>
                <TableHeader>% On Track</TableHeader>
              </tr>
            </thead>
            <tbody>
              {groups.length > 0 ? (
                groups.map(group => {
                  const daysAgo = getDaysAgo(group.created_at);
                  const onTrackPercentage = getOnTrackPercentage(group);
                  
                  return (
                    <TableRow 
                      key={group.id}
                      onClick={() => onGroupSelect(group)}
                    >
                      <TableCell>
                        <strong>{group.name}</strong>
                      </TableCell>
                      <TableCell>
                        {daysAgo === 0 ? 'Today' : 
                         daysAgo === 1 ? '1 day ago' : 
                         `${daysAgo} days ago`}
                      </TableCell>
                      <TableCell>{group.member_count}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ProgressBar>
                            <ProgressFill progress={onTrackPercentage} />
                          </ProgressBar>
                          <span style={{ fontSize: '12px', color: brandColors.text.gray, minWidth: '35px' }}>
                            {onTrackPercentage}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan="4">
                    <EmptyState>
                      No planning groups created yet.
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
        </PlansGrid>
      </ContentWrapper>
    </Container>
  );
};

export default ActionPlanDashboard; 